import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiService } from "../../services/api";

/** Muted teal to match reference card */
const TEAL = "rgb(91, 164, 164)";

const WEEK: { full: string; letter: string }[] = [
  { full: "Monday", letter: "M" },
  { full: "Tuesday", letter: "T" },
  { full: "Wednesday", letter: "W" },
  { full: "Thursday", letter: "T" },
  { full: "Friday", letter: "F" },
  { full: "Saturday", letter: "S" },
  { full: "Sunday", letter: "S" },
];

function normalizeEndpoint(apiname: string | undefined): string {
  let endpoint = (apiname || "").trim();
  if (!endpoint) {
    return "/pabws/hrms/attendance/holidays";
  }
  if (!endpoint.startsWith("/")) {
    endpoint = `/g360/${endpoint}`;
  }
  return endpoint;
}

function normalizeDayName(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  const low = s.toLowerCase();
  for (const { full } of WEEK) {
    if (full.toLowerCase() === low) return full;
  }
  return null;
}

export type WeeklyOffCalendarProps = {
  apiname?: string;
  config?: { apiname?: string; title?: string };
  title?: string;
};

const WeeklyOffCalendar: React.FC<WeeklyOffCalendarProps> = ({
  apiname,
  config,
  title,
}) => {
  const sectionTitle = (title ?? config?.title ?? "").trim();
  const endpoint = normalizeEndpoint(apiname || config?.apiname);

  const [weeklyOffDays, setWeeklyOffDays] = useState<string[]>([]);
  const [calendarYear, setCalendarYear] = useState<number | null>(null);
  const [nextHoliday, setNextHoliday] = useState<{
    holiday_date?: string;
    holiday_name?: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** focused row for title + bar (0–6, Mon–Sun) */
  const [focusIdx, setFocusIdx] = useState(0);

  const offSet = useMemo(() => {
    const s = new Set<string>();
    weeklyOffDays.forEach((d) => {
      const n = normalizeDayName(d);
      if (n) s.add(n);
    });
    return s;
  }, [weeklyOffDays]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.get<{
        weekly_off_days?: string[];
        calendar_year?: number;
        data?: Array<{ holiday_date?: string; holiday_name?: string }>;
      }>(endpoint, { skip: 0, limit: 50 }, { includeAuthToken: true });

      const days = Array.isArray(res?.weekly_off_days) ? res.weekly_off_days : [];
      setWeeklyOffDays(days);

      const cy =
        res?.calendar_year !== undefined && res?.calendar_year !== null
          ? Number(res.calendar_year)
          : null;
      setCalendarYear(Number.isFinite(cy) ? cy : null);

      const rows = Array.isArray(res?.data) ? res.data : [];
      setNextHoliday(rows.length > 0 ? rows[0] : null);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to load";
      setError(msg);
      setWeeklyOffDays([]);
      setNextHoliday(null);
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    load();
  }, [load]);

  // Default focus: today if it is a weekly off, else first weekly off in Mon–Sun order, else Monday
  useEffect(() => {
    if (loading) return;
    const today = new Date();
    const js = today.getDay(); // 0 Sun … 6 Sat
    const monFirstIdx = js === 0 ? 6 : js - 1;
    const todayFull = WEEK[monFirstIdx].full;
    if (offSet.has(todayFull)) {
      setFocusIdx(monFirstIdx);
      return;
    }
    const firstOff = WEEK.findIndex((w) => offSet.has(w.full));
    setFocusIdx(firstOff >= 0 ? firstOff : 0);
  }, [loading, offSet]);

  const focused = WEEK[focusIdx];
  const focusedIsOff = offSet.has(focused.full);

  const titleLine = useMemo(() => {
    if (focusedIsOff) {
      return `${focused.full} (full day off)`;
    }
    return `${focused.full} (working day)`;
  }, [focused, focusedIsOff]);

  const subtitle = useMemo(() => {
    if (nextHoliday?.holiday_date && nextHoliday?.holiday_name) {
      return `Next holiday: ${nextHoliday.holiday_name} · ${nextHoliday.holiday_date}`;
    }
    if (calendarYear) {
      return `Calendar year ${calendarYear}`;
    }
    return "";
  }, [nextHoliday, calendarYear]);

  return (
    <div
      className="rounded-2xl border border-gray-200/90 bg-white p-5 shadow-sm dark:border-gray-600 dark:bg-gray-800"
      style={{ maxWidth: 420 }}
    >
      {sectionTitle ? (
        <h3 className="mb-4 text-sm font-semibold tracking-tight text-gray-900 dark:text-gray-100">
          {sectionTitle}
        </h3>
      ) : null}

      {error && (
        <p className="mb-2 text-sm text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}

      {loading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      ) : (
        <>
          {/* Day pills — same layout as reference */}
          <div className="mb-4 flex justify-between gap-1 sm:gap-2">
            {WEEK.map((d, i) => {
              const isOff = offSet.has(d.full);
              const isFocus = i === focusIdx;
              return (
                <button
                  key={d.full}
                  type="button"
                  onClick={() => setFocusIdx(i)}
                  title={d.full}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition-all sm:h-11 sm:w-11"
                  style={{
                    border: isFocus
                      ? `2px solid ${TEAL}`
                      : isOff
                        ? `1px solid ${TEAL}`
                        : "1px solid rgb(229 231 235)",
                    backgroundColor: isOff ? TEAL : "white",
                    color: isOff ? "white" : "rgb(55 65 81)",
                    transform: isFocus ? "scale(1.06)" : undefined,
                    boxShadow: isFocus ? "0 2px 8px rgba(91,164,164,0.35)" : undefined,
                  }}
                >
                  {d.letter}
                </button>
              );
            })}
          </div>

          {/* Title row — mirrors "Today (9:30 AM - 6:30 PM)" style */}
          <p className="mb-2 text-[15px] font-medium text-gray-800 dark:text-gray-200">{titleLine}</p>
          {subtitle ? (
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
          ) : null}

          {/* Capsule timeline — teal / gray / teal; full-day off uses wider center gray as visual variant */}
          <div
            className="mb-4 flex h-3 w-full overflow-hidden rounded-full"
            style={{ background: "rgb(243 244 246)" }}
          >
            {focusedIsOff ? (
              <div className="h-full w-full rounded-full" style={{ background: TEAL }} />
            ) : (
              <>
                <div className="h-full rounded-l-full" style={{ flex: "0 0 42%", background: TEAL }} />
                <div className="h-full bg-gray-200 dark:bg-gray-600" style={{ flex: "0 0 16%" }} />
                <div className="h-full rounded-r-full" style={{ flex: "1 1 42%", background: TEAL }} />
              </>
            )}
          </div>

          {/* Footer — duration left, icon + label right */}
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>
              {weeklyOffDays.length > 0
                ? `Weekly off: ${weeklyOffDays.join(", ")}`
                : "No weekly off configured"}
            </span>
            <span className="flex items-center gap-1.5">
              <span aria-hidden className="text-base leading-none">
                ☕
              </span>
              <span>{focusedIsOff ? "Full day" : "—"}</span>
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default WeeklyOffCalendar;
